import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { ScrollText, Filter } from "lucide-react";

const ACTION_COLORS = {
  "pos.pin.approve": "bg-green-100 text-green-800",
  "pos.pin.reject": "bg-red-100 text-red-800",
  "staff.create": "bg-blue-100 text-blue-800",
  "staff.update": "bg-amber-100 text-amber-800",
  "staff.deactivate": "bg-gray-200 text-gray-800",
};

export default function AdminAudit() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ email: "", action: "", target_id: "", since: "", until: "" });

  const load = () => {
    setLoading(true);
    const params = {};
    Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
    params.limit = 200;
    api.get("/admin/audit", { params }).then((r) => {
      setItems(r.data.items);
      setTotal(r.data.total);
    }).catch((err) => {
      toast.error(err?.response?.data?.detail || "Failed to load audit log");
    }).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  return (
    <div className="p-6" data-testid="admin-audit-page">
      <div className="mb-6">
        <div className="ipv-eyebrow text-[#FFC220]">Owner-only</div>
        <h1 className="font-display font-black text-3xl tracking-tight text-[#0A1C40] flex items-center gap-3">
          <ScrollText className="w-7 h-7" /> Audit Log
        </h1>
        <div className="text-xs text-gray-600 mt-1">Every user action that touches money or a staff account is recorded here. Append-only — nothing can be edited or deleted.</div>
      </div>

      {/* Filters */}
      <div className="mb-4 border border-gray-200 p-4 grid grid-cols-2 lg:grid-cols-6 gap-3 items-end" data-testid="audit-filters">
        <div>
          <label className="ipv-eyebrow block mb-1">Email contains</label>
          <input className="input-base text-sm" value={filters.email} onChange={(e) => setFilters({ ...filters, email: e.target.value })} placeholder="e.g. associate" data-testid="audit-filter-email" />
        </div>
        <div>
          <label className="ipv-eyebrow block mb-1">Action</label>
          <input className="input-base text-sm" value={filters.action} onChange={(e) => setFilters({ ...filters, action: e.target.value })} placeholder="e.g. pos. or staff.create" data-testid="audit-filter-action" />
        </div>
        <div>
          <label className="ipv-eyebrow block mb-1">Target ID</label>
          <input className="input-base text-sm" value={filters.target_id} onChange={(e) => setFilters({ ...filters, target_id: e.target.value })} placeholder="order or user ID" data-testid="audit-filter-target" />
        </div>
        <div>
          <label className="ipv-eyebrow block mb-1">Since</label>
          <input type="datetime-local" className="input-base text-sm" value={filters.since} onChange={(e) => setFilters({ ...filters, since: e.target.value })} data-testid="audit-filter-since" />
        </div>
        <div>
          <label className="ipv-eyebrow block mb-1">Until</label>
          <input type="datetime-local" className="input-base text-sm" value={filters.until} onChange={(e) => setFilters({ ...filters, until: e.target.value })} data-testid="audit-filter-until" />
        </div>
        <div>
          <button onClick={load} className="btn-navy inline-flex items-center gap-2 w-full justify-center" data-testid="audit-apply">
            <Filter className="w-4 h-4" /> Apply
          </button>
        </div>
      </div>

      <div className="text-xs text-gray-500 mb-2">{loading ? "Loading…" : `Showing ${items.length} of ${total} entries`}</div>

      <div className="border border-gray-200 overflow-x-auto">
        <table className="w-full text-sm" data-testid="audit-table">
          <thead className="bg-gray-50 text-left">
            <tr>
              <th className="p-2 ipv-eyebrow">When</th>
              <th className="p-2 ipv-eyebrow">Actor</th>
              <th className="p-2 ipv-eyebrow">Role</th>
              <th className="p-2 ipv-eyebrow">Action</th>
              <th className="p-2 ipv-eyebrow">Target</th>
              <th className="p-2 ipv-eyebrow">Detail</th>
              <th className="p-2 ipv-eyebrow">IP</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 font-mono text-[11px]">
            {items.map((e) => (
              <tr key={e.id}>
                <td className="p-2 whitespace-nowrap">{e.at?.slice(0, 19).replace("T", " ")}</td>
                <td className="p-2">{e.actor_email || <span className="text-gray-400 italic">system</span>}</td>
                <td className="p-2">{e.actor_role || "—"}</td>
                <td className="p-2">
                  <span className={`inline-block px-1.5 py-0.5 rounded-sm text-[10px] font-bold ${ACTION_COLORS[e.action] || "bg-gray-100 text-gray-800"}`}>{e.action}</span>
                </td>
                <td className="p-2">{e.target_type ? `${e.target_type}:${(e.target_id || "").slice(0, 8)}` : "—"}</td>
                <td className="p-2 max-w-md truncate" title={JSON.stringify(e.meta || e.after || e.before || {})}>
                  {e.meta?.reason || e.meta?.why || (e.after && `→ ${JSON.stringify(e.after).slice(0, 60)}`) || "—"}
                </td>
                <td className="p-2">{e.ip || "—"}</td>
              </tr>
            ))}
            {!items.length && !loading && (
              <tr><td colSpan={7} className="p-6 text-center text-sm text-gray-500">No audit entries match your filters.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
